import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';

import '@awesome.me/webawesome/dist/components/button/button.js';
import '@awesome.me/webawesome/dist/components/callout/callout.js';

import { nsLayoutStyles } from '../../../../ns-styles/index.js';

import NSJobComponent from '../../job.js';

import JobSchema from '../../../../models/job-schema.js';

import { claimJob, isUnclaimed } from '../../../../helpers/job-claim.js';
import { lambdaRefusal } from '../../../../helpers/public-ledger-balance-api.js';
import { personDisplayName } from '../../../../helpers/person.js';
import type Person from '../../../../models/person.js';
import { createLogger } from '../../../../ns-services/logger.js';

import '../../../../ns-elements/ns-job-layout.js';
import '../../../../ns-elements/ns-detail-list.js';
import definition from '@job-schemas/support/hmrc-no-reviewer.json';
import { jobSchema } from '../../job-schema.js';
import { reviewerFound, reviewerFoundMessage, type ReviewerFound } from './hmrc-no-reviewer-logic.js';

const logger = createLogger('ns-support-hmrc-no-reviewer');

/**
 * The nodeStream support card the hmrc/trust-score-review cron raises when no one at a published company can re-score its
 * HMRC trust score (no validator and no verified, employed director). Support claims it, contacts the company, and marks
 * it resolved. The company's HMRC score still falls to 0 after 14 days unless someone there completes a review.
 *
 * The card is never closed for support: once someone at the company can review again, the cron gives them the review card and
 * notes it on this job (REVIEWER_FOUND), and the card says so until support marks it resolved. The note is cleared if nobody
 * can review again. The card follows its job, so a note written while it is open shows at once.
 */
@customElement(NSSupportHMRCNoReviewer.is)
export default class NSSupportHMRCNoReviewer extends NSJobComponent {
  static get is() {
    return 'ns-support-hmrc-no-reviewer';
  }

  static get schema(): JobSchema {
    return jobSchema(definition, { outcomes: { resolved: { type: 'hidden' } } });
  }

  static styles = [nsLayoutStyles, ...super.styles];

  @state()
  private __busy = false;

  @state()
  private __reviewerFound: ReviewerFound | null = null;

  // The noted reviewer's name, once read ('' when it cannot be), and whose it is
  @state()
  private __reviewerName: string | null = null;

  private __reviewerNameFor: string | null = null;

  async connectedCallback() {
    await super.connectedCallback();
    await this.updateComplete;
    if (!this._job) return;
    this.__readNote();
    // The hmrc/trust-score-review cron writes or clears the note while the card may be open
    this._subscriptions.subscribe(`job.${this._job.id}.*`, () => this.__readNote());
  }

  private __readNote() {
    this.__reviewerFound = reviewerFound(this._job?.metadata);
    void this.__loadReviewerName();
  }

  // Every user may read a person's name, so support can name the reviewer; without it the card says "Someone"
  private async __loadReviewerName() {
    const personId = this.__reviewerFound?.personId ?? null;
    if (personId === this.__reviewerNameFor) return;
    this.__reviewerNameFor = personId;
    this.__reviewerName = null;
    if (!personId || !this._db) return;
    try {
      const person = (await this._db.getById('people', personId)) as Person | undefined;
      if (this.__reviewerNameFor === personId) this.__reviewerName = person ? personDisplayName(person) : '';
    } catch (err) {
      logger.warn('Unable to read who can review again', err);
      if (this.__reviewerNameFor === personId) this.__reviewerName = '';
    }
  }

  private get __companyName(): string {
    const name = this._job?.inputValues?.subjectCompanyName;
    return typeof name === 'string' && name ? name : 'This company';
  }

  private async __claim() {
    const token = this._auth?.token;
    if (!this._job || !token || this.__busy) return;
    this.__busy = true;
    this.clearError();
    try {
      await claimJob(token, this._job.id);
      this._job = { ...this._job, assignedTo: this._directory?.me?.id };
    } catch (err) {
      this.showError(lambdaRefusal(err, 'Unable to claim this job.'));
    } finally {
      this.__busy = false;
    }
  }

  private async __resolved() {
    if (this.__busy) return;
    this.__busy = true;
    this.clearError();
    try {
      await this.outcome('resolved');
    } catch (err) {
      this.showError(err, 'Unable to mark this resolved. Please try again.');
    } finally {
      this.__busy = false;
    }
  }

  render() {
    const companyId = this._job?.inputValues?.subjectCompanyId;
    const found = this.__reviewerFound;
    return html`
      <ns-job-layout>
        <p slot="intro">
          ${found
            ? `${this.__companyName}'s HMRC trust score is due for review, and someone there can now do it.`
            : `${this.__companyName}'s HMRC trust score is due for review, and no one there can do it.`}
        </p>
        <div class="job-panel">
          ${found
            ? html`
                <wa-callout variant="success" appearance="filled-outlined">
                  ${reviewerFoundMessage(this.__companyName, found, this.__reviewerName)}
                </wa-callout>
              `
            : nothing}
          <h3>Who to contact</h3>
          <ns-detail-list
            .rows=${[
              { label: 'Company', value: this.__companyName },
              { label: 'Company ID', value: typeof companyId === 'string' ? companyId : null },
            ]}
          ></ns-detail-list>
          <p>
            The person who connected the company to HMRC can no longer act for it, and the company has no verified
            director with an active profile. Contact the company and ask a director to verify their profile, or someone
            to connect HMRC again. Their review card goes to them once they can.
          </p>
          <p>Mark this resolved once you have contacted them.</p>
        </div>
        <div slot="aside" class="primary-info">
          <span class="info-icon" aria-hidden="true">i</span>
          <h3>What happens next</h3>
          <p>
            If no one at the company completes an HMRC review within 14 days of it falling due, the HMRC part of its
            trust score falls to 0, with the reason "HMRC review not completed". The rest of its score stays as it is.
          </p>
        </div>
      </ns-job-layout>
    `;
  }

  renderActions() {
    const job = this._job;
    if (!job || job.status === 'COMPLETE') return nothing;
    if (isUnclaimed(job)) {
      return html`
        <wa-button variant="brand" type="button" ?disabled=${this.__busy} @click=${() => this.__claim()}>
          Claim
        </wa-button>
      `;
    }
    return html`
      <wa-button variant="brand" type="button" ?disabled=${this.__busy} @click=${() => this.__resolved()}>
        Resolved
      </wa-button>
    `;
  }
}
